import { useCallback, useReducer } from "react";

type AlarmSetpointListStateType = {
    // Options state
    filterText: string;
    groupByAlarmState: boolean;

    // Other state
    isOptionsDirty: boolean;
};

const initialState: AlarmSetpointListStateType = {
    filterText: "",
    groupByAlarmState: true,
    isOptionsDirty: false
};

enum AlarmSetpointListActionEnum {
    ResetOptionsRequested = "RESET_OPTIONS_REQUESTED",
    FilterTextChanged = "FILTER_TEXT_CHANGED",
    GroupByAlarmStateChanged = "GROUP_BY_ALARM_STATE_CHANGED"
}

type AlarmSetpointListActionTypes = {
    type: AlarmSetpointListActionEnum.ResetOptionsRequested;
} | {
    type: AlarmSetpointListActionEnum.FilterTextChanged;
    filterText: string;
} | {
    type: AlarmSetpointListActionEnum.GroupByAlarmStateChanged;
    groupByAlarmState: boolean;
};

function reducer(state: AlarmSetpointListStateType, action: AlarmSetpointListActionTypes): AlarmSetpointListStateType {

    const copy = { ...state };
    switch (action.type) {

        case AlarmSetpointListActionEnum.ResetOptionsRequested:
            copy.filterText = initialState.filterText;
            copy.groupByAlarmState = initialState.groupByAlarmState;
            copy.isOptionsDirty = false;
            return copy;

        case AlarmSetpointListActionEnum.FilterTextChanged:
            copy.filterText = action.filterText;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case AlarmSetpointListActionEnum.GroupByAlarmStateChanged:
            copy.groupByAlarmState = action.groupByAlarmState;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;
    }
}

function isOptionsDirty(state: AlarmSetpointListStateType) {
    return state.filterText !== initialState.filterText ||
        state.groupByAlarmState !== initialState.groupByAlarmState;
}

type UseAlarmSetpointListStateType = {
    state: AlarmSetpointListStateType;
    resetOptions: () => void;
    setFilterText: (filterText: string) => void;
    setGroupByAlarmState: (groupByAlarmState: boolean) => void;
};

export default function useAlarmSetpointListState(): UseAlarmSetpointListStateType {
    const [state, dispatch] = useReducer(reducer, initialState);

    const resetOptions = useCallback(() => dispatch({
            type: AlarmSetpointListActionEnum.ResetOptionsRequested
        }), []);

    const setFilterText = useCallback((filterText: string) => dispatch({
            type: AlarmSetpointListActionEnum.FilterTextChanged,
            filterText: filterText
        }), []);

    const setGroupByAlarmState = useCallback((groupByAlarmState: boolean) => dispatch({
            type: AlarmSetpointListActionEnum.GroupByAlarmStateChanged,
            groupByAlarmState: groupByAlarmState
        }), []);

    return {
        state,
        resetOptions,
        setFilterText,
        setGroupByAlarmState
    };
}    
